import React, { useState, useRef, useEffect } from 'react';
import { useTouchDevice } from '../../hooks/useTouchDevice';
import { PROJECT_TITLE_AUTOFILL_ATTRS } from '../../utils/projectTitleInput';
import { useAutofillShield } from './ProjectTitleInput';

/**
 * Inline editable name component.
 * Double-click to edit, Enter to save, Escape to cancel.
 */
function EditableName({ value, onSave, className = '', showEditIcon = true, editLabel = 'Edit name', forceEdit = 0 }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef(null);
  const isTouch = useTouchDevice();
  const { readOnly, unlockOnFocus } = useAutofillShield();

  useEffect(() => {
    if (!forceEdit) return;
    setEditValue(value);
    setIsEditing(true);
    // The tick is the request. The name at click time is what we edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceEdit]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleDoubleClick = (e) => {
    e.stopPropagation();
    setEditValue(value);
    setIsEditing(true);
  };

  const handleSave = async () => {
    const trimmedValue = editValue.trim();
    if (trimmedValue && trimmedValue !== value) {
      try {
        await onSave(trimmedValue);
      } catch (err) {
        console.error('Failed to save name:', err);
        setEditValue(value); // Revert on error
      }
    } else {
      setEditValue(value); // Revert if empty or unchanged
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(value);
      setIsEditing(false);
    }
  };

  const handleBlur = () => {
    handleSave();
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        className={`editable-name-input ${className}`}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onClick={(e) => e.stopPropagation()}
        onFocus={unlockOnFocus}
        readOnly={readOnly}
        aria-label={editLabel}
        {...PROJECT_TITLE_AUTOFILL_ATTRS}
      />
    );
  }

  return (
    <span
      className={`editable-name ${isTouch ? 'editable-name--touch' : ''} ${className}`}
      onDoubleClick={handleDoubleClick}
      title="Double-click to edit"
    >
      {value}
      {showEditIcon && (
        <button
          className="edit-icon"
          onClick={(e) => {
            e.stopPropagation();
            setEditValue(value);
            setIsEditing(true);
          }}
          title={editLabel}
          aria-label={editLabel}
        >
          ✎
        </button>
      )}
    </span>
  );
}

export default EditableName;
